import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material/styles";
import NewTranscriptionLangDropdown from "../NewStudio/NewTranscriptionLangDropdown";
import { formatToReadableLargeTitle, LibrettoProjectTypes } from "../../utils/utils";
import { isSupportedChromeEdgeOrBrave, unsupportedEditorBrowserMessage } from "../../utils/browserSupport";
import { validateFileForUpload } from "../../utils/fileValidation";
import { ExtractMediaFileMetadata } from "../../storage/Media";
import { useAssetStore } from "../../storage/AssetStore";
import { trackSessionEvent } from "../../analytics/sessionEvents";

const viewStates = {
    SELECT_FILE: "select-file",
    REVIEW_AND_RUN: "review-and-run",
    RUNNING: "running",
};

const waveformColumns = [18, 34, 26, 40, 22, 30, 38, 24, 32, 20, 36, 18, 26, 34, 28];
const imageFilePattern = /\.(bmp|jpe?g|png|gif|webp)$/i;
const defaultEditForClarityIntensity = "medium";
const intensityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "heavy", label: "Heavy" },
];

const getUploadFiletype = (file) => (file?.type?.startsWith("video/") ? "video" : "audio");

const formatFileName = (name = "") => {
    const trimmed = formatToReadableLargeTitle(name || "");
    if (trimmed.length <= 74) return trimmed;
    return `${trimmed.slice(0, 74)}...`;
};

const formatMediaDuration = (durationSec) => {
    if (!Number.isFinite(durationSec) || durationSec <= 0) return "";

    const totalSeconds = Math.max(0, Math.round(durationSec));
    if (totalSeconds < 60) {
        return `${totalSeconds}s`;
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }

    return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const AudioWaveformPreview = () => {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                borderRadius: "12px",
                background: "linear-gradient(180deg, var(--app-bg-surface) 0%, var(--app-bg-subtle) 100%)",
                border: "1px solid var(--app-border-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                px: "26px",
            }}
        >
            {waveformColumns.map((height, idx) => (
                <Box
                    key={`${height}-${idx}`}
                    sx={{
                        width: "6px",
                        height: `${height}px`,
                        borderRadius: "999px",
                        backgroundColor: idx % 3 === 0 ? "#2B6BFD" : "var(--app-icon-primary)",
                        opacity: idx % 3 === 0 ? 0.8 : 0.65,
                    }}
                />
            ))}
        </Box>
    );
};

const MediaPreviewCard = ({ file, showMeta = false }) => {
    const [previewUrl, setPreviewUrl] = useState("");
    const [aspectRatio, setAspectRatio] = useState("landscape");
    const [durationLabel, setDurationLabel] = useState("");

    const isVideo = Boolean(file?.type?.startsWith("video/"));
    const isAudio = Boolean(file?.type?.startsWith("audio/"));

    useEffect(() => {
        if (!isVideo || !file) {
            setPreviewUrl("");
            setAspectRatio("landscape");
            if (!isAudio) {
                setDurationLabel("");
            }
            return;
        }

        let cancelled = false;
        const videoUrl = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.src = videoUrl;
        video.preload = "metadata";
        video.muted = true;

        const cleanupVideoUrl = () => {
            URL.revokeObjectURL(videoUrl);
        };

        video.onloadedmetadata = () => {
            if (cancelled) return;
            const ratio = (video.videoWidth || 1) / (video.videoHeight || 1);
            if (ratio > 1.1) setAspectRatio("landscape");
            else if (ratio < 0.9) setAspectRatio("portrait");
            else setAspectRatio("square");
            setDurationLabel(formatMediaDuration(video.duration));

            const seekTarget = Number.isFinite(video.duration) && video.duration > 1 ? 1 : 0;
            const safeTarget = Math.max(0, Math.min(seekTarget, (video.duration || 0) - 0.05));
            video.currentTime = Number.isFinite(safeTarget) ? safeTarget : 0;
        };

        video.onseeked = () => {
            if (cancelled) return;
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                cleanupVideoUrl();
                return;
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            setPreviewUrl(canvas.toDataURL("image/jpeg", 0.85));
            cleanupVideoUrl();
        };

        video.onerror = () => {
            cleanupVideoUrl();
        };

        return () => {
            cancelled = true;
            cleanupVideoUrl();
        };
    }, [file, isVideo]);

    useEffect(() => {
        if (!isAudio || !file) {
            if (!isVideo) {
                setDurationLabel("");
            }
            return;
        }

        let cancelled = false;
        const audioUrl = URL.createObjectURL(file);
        const audio = document.createElement("audio");
        audio.src = audioUrl;
        audio.preload = "metadata";

        audio.onloadedmetadata = () => {
            if (cancelled) return;
            setDurationLabel(formatMediaDuration(audio.duration));
            URL.revokeObjectURL(audioUrl);
        };

        audio.onerror = () => {
            if (cancelled) return;
            setDurationLabel("");
            URL.revokeObjectURL(audioUrl);
        };

        return () => {
            cancelled = true;
            URL.revokeObjectURL(audioUrl);
        };
    }, [file, isAudio, isVideo]);

    const mediaStyle = (() => {
        if (aspectRatio === "portrait") {
            return {
                width: "auto",
                height: "100%",
                maxWidth: "100%",
                objectFit: "contain",
            };
        }
        if (aspectRatio === "square") {
            return {
                width: "auto",
                height: "100%",
                maxWidth: "100%",
                objectFit: "contain",
            };
        }
        return {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        };
    })();

    const metaText = [formatFileName(file?.name), durationLabel].filter(Boolean).join(" \u2022 ");

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    height: "300px",
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: "1px solid var(--app-border-subtle)",
                    backgroundColor: "var(--app-bg-subtle)",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {isVideo && previewUrl ? (
                    <img src={previewUrl} alt="Uploaded video preview" style={mediaStyle} />
                ) : null}
                {isAudio ? <AudioWaveformPreview /> : null}
                {!isVideo && !isAudio ? (
                    <Box
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "14px",
                            color: "var(--app-text-muted)",
                        }}
                    >
                        Preview unavailable for this file type.
                    </Box>
                ) : null}
            </Box>
            {showMeta && metaText ? (
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--app-text-muted)",
                        lineHeight: "18px",
                        textAlign: "left",
                    }}
                >
                    {metaText}
                </Box>
            ) : null}
        </Box>
    );
};

const Header = ({ onClose, subtitle }) => {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                width: "100%",
                gap: "16px",
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", pr: "10px" }}>
                <Box
                    sx={{
                        color: "var(--app-text-primary)",
                        fontFamily: "Inter",
                        fontSize: "20px",
                        fontWeight: 600,
                        lineHeight: "28px",
                        letterSpacing: "-0.01em",
                    }}
                >
                    Clean up a recording
                </Box>
                <Box
                    sx={{
                        color: "var(--app-text-muted)",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                    }}
                >
                    {subtitle}
                </Box>
            </Box>
            <Box
                sx={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "10px",
                    border: "1px solid var(--app-border-subtle)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "var(--app-text-muted)",
                    "&:hover": {
                        backgroundColor: "var(--app-bg-subtle)",
                        color: "var(--app-text-primary)",
                    },
                }}
                onClick={onClose}
            >
                <CloseIcon sx={{ fontSize: "20px" }} />
            </Box>
        </Box>
    );
};

const SelectFileState = ({ onFileChoose, validationMessage, isDarkMode = false, onUseSample }) => {
    const inputRef = useRef(null);
    const dragDepthRef = useRef(0);
    const [isDragActive, setIsDragActive] = useState(false);

    const handleChange = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;
        await onFileChoose(file);
        if (e?.target) {
            e.target.value = "";
        }
    };

    const resetDragState = () => {
        dragDepthRef.current = 0;
        setIsDragActive(false);
    };

    const getDroppedFile = (e) => {
        const items = Array.from(e?.dataTransfer?.items || []);
        for (const item of items) {
            if (item?.kind !== "file") continue;
            const file = item.getAsFile?.();
            if (file) return file;
        }
        const files = e?.dataTransfer?.files;
        return files?.[0] || null;
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setIsDragActive(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e?.dataTransfer) {
            e.dataTransfer.dropEffect = "copy";
        }
        if (!isDragActive) {
            setIsDragActive(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragActive(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = getDroppedFile(e);
        resetDragState();
        if (!file) return;
        await onFileChoose(file);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                component="label"
                htmlFor="edit-for-clarity-upload"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                    width: "100%",
                    height: "300px",
                    borderRadius: "14px",
                    border: `1px dashed ${isDragActive ? "#2B6BFD" : "var(--app-border-muted)"}`,
                    backgroundColor: isDragActive
                        ? (isDarkMode ? "rgba(43, 107, 253, 0.18)" : "#F3F8FF")
                        : "var(--app-bg-canvas)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                        backgroundColor: isDarkMode ? "rgba(43, 107, 253, 0.12)" : "var(--app-bg-subtle)",
                        borderColor: "var(--app-border-muted)",
                    },
                }}
            >
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        lineHeight: "20px",
                    }}
                >
                    Upload video or audio
                </Box>
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "var(--app-text-muted)",
                        lineHeight: "18px",
                    }}
                >
                    {isDragActive ? "Drop file to upload." : "Click to browse or drag and drop your file."}
                </Box>
            </Box>
            <input
                ref={inputRef}
                id="edit-for-clarity-upload"
                type="file"
                accept="video/*, audio/*"
                hidden
                onChange={handleChange}
            />
            {validationMessage ? (
                <Box
                    sx={{
                        mt: "12px",
                        px: "12px",
                        py: "10px",
                        borderRadius: "10px",
                        border: "1px solid #FECACA",
                        backgroundColor: "#FEF2F2",
                        color: "#B91C1C",
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                    }}
                >
                    {validationMessage}
                </Box>
            ) : null}
            {onUseSample && (
                <Box
                    onClick={onUseSample}
                    sx={{
                        mt: "14px",
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--app-text-muted)",
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "color 0.15s ease",
                        "&:hover": { color: "var(--app-text-primary)" },
                    }}
                >
                    or try with a{" "}
                    <Box
                        component="span"
                        sx={{
                            color: "#2B6BFD",
                            fontWeight: 600,
                            "&:hover": { textDecoration: "underline" },
                        }}
                    >
                        sample recording
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const RunningState = ({
    selectedFile,
    statusTitle,
    statusDetail,
    errorMessage,
    onChooseDifferentFile,
}) => {
    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "18px" }}>
            <MediaPreviewCard file={selectedFile} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        lineHeight: "24px",
                    }}
                >
                    {statusTitle}
                </Box>
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "var(--app-text-muted)",
                        lineHeight: "20px",
                    }}
                >
                    {statusDetail}
                </Box>
            </Box>
            {errorMessage ? (
                <Box
                    sx={{
                        px: "12px",
                        py: "10px",
                        borderRadius: "10px",
                        border: "1px solid #FECACA",
                        backgroundColor: "#FEF2F2",
                        color: "#B91C1C",
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                    }}
                >
                    {errorMessage}
                </Box>
            ) : (
                <LinearProgress
                    variant="indeterminate"
                    sx={{
                        borderRadius: "999px",
                        height: "8px",
                        backgroundColor: "var(--app-border-subtle)",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: "999px",
                            backgroundColor: "#2B6BFD",
                        },
                    }}
                />
            )}
            {onChooseDifferentFile ? (
                <Box
                    sx={{
                        width: "fit-content",
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--app-text-muted)",
                        cursor: "pointer",
                        "&:hover": {
                            color: "var(--app-text-primary)",
                        },
                    }}
                    onClick={onChooseDifferentFile}
                >
                    Choose a different file
                </Box>
            ) : null}
        </Box>
    );
};

const ReviewState = ({
    selectedFile,
    selectedLanguage,
    setSelectedLanguage,
    editForClarityIntensity,
    onSelectIntensity,
    advancedOpen,
    setAdvancedOpen,
    onRun,
    onSwapFile,
    runValidationMessage,
    isStartingRun,
    isDarkMode = false,
}) => {
    const selectedIndex = Math.max(
        0,
        intensityOptions.findIndex((option) => option.value === editForClarityIntensity),
    );

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
            <MediaPreviewCard file={selectedFile} showMeta={true} />

            <Box
                sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "var(--app-text-muted)",
                    lineHeight: "20px",
                }}
            >
                Remove filler words, digressions, blather - all the obvious cuts.
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "var(--app-bg-surface)",
                    border: "1px solid var(--app-border-subtle)",
                    borderRadius: "12px",
                    px: "14px",
                    py: "14px",
                    gap: "10px",
                }}
            >
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        lineHeight: "20px",
                    }}
                >
                    Edit intensity
                </Box>

                <Box sx={{ px: "4px", py: "6px" }}>
                    <Box
                        sx={{
                            position: "relative",
                            height: "1.5px",
                            backgroundColor: "var(--app-border-muted)",
                            borderRadius: "1px",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                height: "100%",
                                width: `${selectedIndex * 50}%`,
                                backgroundColor: "var(--app-text-primary)",
                                borderRadius: "1px",
                                transition: "width 0.15s ease",
                            }}
                        />
                        {intensityOptions.map((option, idx) => (
                            <Box
                                key={option.value}
                                onClick={() => onSelectIntensity(option.value)}
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: `${idx * 50}%`,
                                    transform: "translate(-50%, -50%)",
                                    width: idx === selectedIndex ? 11 : 6,
                                    height: idx === selectedIndex ? 11 : 6,
                                    borderRadius: "50%",
                                    backgroundColor: idx <= selectedIndex ? "var(--app-text-primary)" : "var(--app-border-muted)",
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                    zIndex: 1,
                                    "&:hover": {
                                        transform: "translate(-50%, -50%) scale(1.15)",
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", mt: "2px" }}>
                    {intensityOptions.map((option) => (
                        <Box
                            key={option.value}
                            onClick={() => onSelectIntensity(option.value)}
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "13px",
                                color: option.value === editForClarityIntensity ? "var(--app-text-primary)" : "var(--app-text-subtle)",
                                fontWeight: option.value === editForClarityIntensity ? 600 : 400,
                                cursor: "pointer",
                                userSelect: "none",
                                transition: "color 0.15s ease",
                            }}
                        >
                            {option.label}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    width: "fit-content",
                    cursor: "pointer",
                    color: "var(--app-text-muted)",
                    "&:hover": {
                        color: "var(--app-text-primary)",
                    },
                }}
                onClick={() => setAdvancedOpen(!advancedOpen)}
            >
                {advancedOpen ? (
                    <KeyboardArrowDownRoundedIcon sx={{ fontSize: "18px" }} />
                ) : (
                    <KeyboardArrowRightRoundedIcon sx={{ fontSize: "18px" }} />
                )}
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        lineHeight: "18px",
                    }}
                >
                    Advanced settings
                </Box>
            </Box>

            {advancedOpen ? (
                <Box
                    sx={{
                        width: "100%",
                        borderRadius: "12px",
                        border: "1px solid var(--app-border-subtle)",
                        backgroundColor: "var(--app-bg-surface)",
                        px: "14px",
                        py: "14px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "var(--app-text-primary)",
                            lineHeight: "18px",
                        }}
                    >
                        Content language
                    </Box>
                    <Box
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "var(--app-text-muted)",
                            lineHeight: "18px",
                        }}
                    >
                        Set language for speech transcription quality.
                    </Box>
                    <Box sx={{ width: "234px" }}>
                        <NewTranscriptionLangDropdown
                            selectedLanguage={selectedLanguage}
                            onChange={setSelectedLanguage}
                            compact={true}
                        />
                    </Box>
                </Box>
            ) : null}

            {runValidationMessage ? (
                <Box
                    sx={{
                        px: "12px",
                        py: "10px",
                        borderRadius: "10px",
                        border: "1px solid #FECACA",
                        backgroundColor: "#FEF2F2",
                        color: "#B91C1C",
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                    }}
                >
                    {runValidationMessage}
                </Box>
            ) : null}

            <Box
                sx={{
                    width: "100%",
                    height: "52px",
                    borderRadius: "999px",
                    backgroundColor: isDarkMode ? "#2B6BFD" : "#1a1a1a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isStartingRun ? "default" : "pointer",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                        backgroundColor: isStartingRun ? (isDarkMode ? "#2B6BFD" : "#1a1a1a") : (isDarkMode ? "#245DDB" : "#111827"),
                    },
                    opacity: isStartingRun ? 0.8 : 1,
                }}
                onClick={isStartingRun ? undefined : onRun}
            >
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#FFFFFF",
                        lineHeight: "20px",
                        letterSpacing: "0.01em",
                    }}
                >
                    {isStartingRun ? "Checking media..." : "Run cleanup"}
                </Box>
            </Box>

            <Box
                sx={{
                    width: "fit-content",
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--app-text-muted)",
                    cursor: "pointer",
                    "&:hover": {
                        color: "var(--app-text-primary)",
                    },
                }}
                onClick={onSwapFile}
            >
                Choose a different file
            </Box>
        </Box>
    );
};

const EditForClarityWorkflow = ({
    onClose,
    projectId = null,
    initialFile = null,
    onRunEditForClarity,
    onEditorReady,
    onUseSample,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const { UploadAsset } = useAssetStore();
    const [selectedFile, setSelectedFile] = useState(initialFile);
    const [selectedLanguage, setSelectedLanguage] = useState("auto");
    const [editForClarityIntensity, setEditForClarityIntensity] = useState(defaultEditForClarityIntensity);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [isStartingRun, setIsStartingRun] = useState(false);
    const [runningTitle, setRunningTitle] = useState("Preparing editor");
    const [runningDetail, setRunningDetail] = useState("Uploading your file and opening the editor.");
    const [runningError, setRunningError] = useState("");
    const [currentView, setCurrentView] = useState(
        initialFile ? viewStates.REVIEW_AND_RUN : viewStates.SELECT_FILE
    );
    const runSettingsRef = useRef({ intensity: defaultEditForClarityIntensity });

    const resetRunningState = () => {
        setRunningTitle("Preparing editor");
        setRunningDetail("Uploading your file and opening the editor.");
        setRunningError("");
        setIsStartingRun(false);
    };

    useEffect(() => {
        if (initialFile) {
            setSelectedFile(initialFile);
            setCurrentView(viewStates.REVIEW_AND_RUN);
            setValidationMessage("");
            resetRunningState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFile]);

    const handleFileChoose = async (file) => {
        const isImageFile = Boolean(file?.type?.startsWith("image/")) || imageFilePattern.test(String(file?.name ?? ""));
        if (isImageFile) {
            setValidationMessage("Images are not supported in Edit for Clarity. Upload an audio or video file.");
            return;
        }

        const validation = await validateFileForUpload(file);
        if (validation.mode !== "local_ok") {
            const extensionText = validation.codec
                ? `${validation.extension} (${validation.codec})`
                : validation.extension;
            setValidationMessage(`${extensionText} files are not supported in this workflow.`);
            return;
        }

        setSelectedFile(file);
        setCurrentView(viewStates.REVIEW_AND_RUN);
        setValidationMessage("");
        resetRunningState();
    };

    const handleRun = async () => {
        if (!selectedFile || isStartingRun) return;
        if (!isSupportedChromeEdgeOrBrave()) {
            setValidationMessage(unsupportedEditorBrowserMessage);
            return;
        }

        runSettingsRef.current = { intensity: editForClarityIntensity };
        trackSessionEvent("started_edit_for_clarity");
        setIsStartingRun(true);
        setValidationMessage("");
        setRunningError("");

        try {
            const uploadProjectId = String(projectId || "").trim();
            let uploadProjectIdResolved = uploadProjectId;
            let uploadAssetIdResolved = "";

            const filetype = getUploadFiletype(selectedFile);
            const metadata = await ExtractMediaFileMetadata({
                file: selectedFile,
                fileType: filetype,
            });

            if (!metadata?.hasAudioStream) {
                setValidationMessage("This file has no audio stream. Edit for Clarity requires audio.");
                return;
            }

            setCurrentView(viewStates.RUNNING);
            setRunningTitle("Preparing editor");
            setRunningDetail("Uploading your file and opening the editor.");

            const assetId = await UploadAsset({
                projectId: uploadProjectId || projectId,
                // Keep the quick-start on the same asset/transcript path as the in-editor
                // Edit for Clarity tool so detections stay consistent across entrypoints.
                projectType: LibrettoProjectTypes.ProjectTypeEditor,
                queueEnhancementOnUpload: true,
                file: selectedFile,
                filetype,
                languageCode: selectedLanguage,
                onUploadInitiated: ({ assetId: serverAssetId, projectId: serverProjectId }) => {
                    const resolvedAssetId = String(serverAssetId || "").trim();
                    if (resolvedAssetId) {
                        uploadAssetIdResolved = resolvedAssetId;
                    }
                    const resolved = String(serverProjectId || "").trim();
                    if (resolved) {
                        uploadProjectIdResolved = resolved;
                    }
                },
            });

            if (!assetId) {
                throw new Error("Upload failed to return an asset id");
            }
            uploadAssetIdResolved = String(assetId || "").trim();

            if (onRunEditForClarity) {
                onRunEditForClarity({
                    file: selectedFile,
                    languageCode: selectedLanguage,
                    assetId: uploadAssetIdResolved,
                    projectId: uploadProjectIdResolved || uploadProjectId,
                    settings: { ...runSettingsRef.current },
                });
            }

            setRunningTitle("Opening editor");
            setRunningDetail("Finishing setup.");

            const payload = {
                assetId: uploadAssetIdResolved,
                projectId: uploadProjectIdResolved || uploadProjectId,
                settings: { ...runSettingsRef.current },
            };

            if (onEditorReady) {
                await Promise.resolve(onEditorReady(payload));
            } else {
                onClose?.();
            }
        } catch (error) {
            console.error("[EditForClarityWorkflow] Upload failed", error);
            setCurrentView(viewStates.RUNNING);
            setRunningTitle("Could not open editor");
            setRunningDetail("We couldn't finish preparing this file.");
            setRunningError("Could not prepare this file. Please try again.");
        } finally {
            setIsStartingRun(false);
        }
    };

    const handleSwapFile = () => {
        setCurrentView(viewStates.SELECT_FILE);
        setAdvancedOpen(false);
        setValidationMessage("");
        resetRunningState();
    };

    const handleSelectIntensity = (value) => {
        const nextValue = String(value || "").trim();
        if (!nextValue || nextValue === editForClarityIntensity) return;
        setEditForClarityIntensity(nextValue);
        trackSessionEvent(`edit_for_clarity_intensity_${nextValue}`);
    };

    const subtitle = currentView === viewStates.SELECT_FILE
        ? "Upload a file to remove filler words, tighten silences, and remove retakes."
        : "Choose an edit intensity and run in one click.";

    return (
        <Box
            sx={{
                width: "715px",
                borderRadius: "12px",
                border: "1px solid var(--app-border-subtle)",
                backgroundColor: "var(--app-bg-surface)",
                px: "24px",
                py: "24px",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
            }}
        >
            <Header onClose={onClose} subtitle={subtitle} />

            {currentView === viewStates.SELECT_FILE ? (
                <SelectFileState
                    onFileChoose={handleFileChoose}
                    validationMessage={validationMessage}
                    isDarkMode={isDarkMode}
                    onUseSample={onUseSample}
                />
            ) : null}

            {currentView === viewStates.REVIEW_AND_RUN ? (
                <ReviewState
                    selectedFile={selectedFile}
                    selectedLanguage={selectedLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                    editForClarityIntensity={editForClarityIntensity}
                    onSelectIntensity={handleSelectIntensity}
                    advancedOpen={advancedOpen}
                    setAdvancedOpen={setAdvancedOpen}
                    onRun={handleRun}
                    onSwapFile={handleSwapFile}
                    runValidationMessage={validationMessage}
                    isStartingRun={isStartingRun}
                    isDarkMode={isDarkMode}
                />
            ) : null}

            {currentView === viewStates.RUNNING ? (
                <RunningState
                    selectedFile={selectedFile}
                    statusTitle={runningTitle}
                    statusDetail={runningDetail}
                    errorMessage={runningError}
                    onChooseDifferentFile={handleSwapFile}
                />
            ) : null}
        </Box>
    );
};

export default EditForClarityWorkflow;
